import { LaunchpadRegisterButton } from "./LaunchpadRegisterButton"
import Typography from "@mui/material/Typography"
import { ConventionLocationStyle } from "app-constants/constants"
import ConventionTime from "components/conventions/ConventionTime"
import useActiveTicketType from "hooks/conventions/useActiveTicketType"
import useHeadingStyles from "hooks/useHeadingStyles"
import useLaunchpadContext from "hooks/useLaunchpadContext"
import useRelativeTime from "hooks/useRelativeTime"
import getConventionAddress from "lib/conventions/getConventionAddress"
import isRegistrationPaid from "lib/conventions/isRegistrationPaid"
import pluralize from "pluralize"
import getFormattedPrice from "utils/getFormattedPrice"
import { pxToRem } from "utils/helpers"
import { makeStyles } from "tss-react/mui-compat"

const useStyles = makeStyles()(
  (theme) => ({
    root: {
      width: "100%",
      display: "flex",
      background: theme.palette.primary.main,
      padding: "40px",
      [theme.breakpoints.down("md")]: {
        flexDirection: "column",
        padding: "20px",
        textAlign: "center",
      },
    },
    heroContainer: {
      position: "relative",
      overflow: "hidden",
      height: "300px",
      width: "535px",
      marginRight: "40px",
      borderRadius: "10px",
      [theme.breakpoints.down("md")]: {
        width: "100%",
        height: "300px",
        marginRight: 0,
      },
    },
    heroImage: {
      position: "absolute",
      width: "auto",
      height: "auto",
      maxWidth: "100%",
      maxHeight: "100%",
      objectFit: "contain",
      borderRadius: "10px",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
    },
    info: {
      display: "flex",
      width: "50%",
      flexDirection: "column",
      [theme.breakpoints.down("md")]: {
        width: "100%",
        marginTop: "30px",
      },
    },
    time: {
      color: "white",
      marginTop: pxToRem(20),
    },
    userCountBox: {
      color: "white",
      display: "flex",
      marginTop: pxToRem(20),
      marginBottom: "auto",
      [theme.breakpoints.down("md")]: {
        alignSelf: "center",
      },
    },
    userCount: {
      fontFamily: "Lato",
      fontSize: "36px",
      fontWeight: 700,
      lineHeight: "22px",
      marginRight: "10px",
    },
    registered: {
      fontFamily: "Lato",
      fontStyle: "italic",
      fontWeight: "normal",
      fontSize: "16px",
      lineHeight: "19px",
      color: "#FFFFFF",
      marginTop: "2rem",
      display: "flex",
      alignItems: "center",
      [theme.breakpoints.down("md")]: {
        marginTop: "30px",
        alignSelf: "center",
      },
    },
    button: {
      backgroundColor: "white",
      marginTop: "2rem",
      [theme.breakpoints.down("md")]: {
        width: "100%",
      },
    },
  }),
)

interface ILaunchpadHeader {}

const LaunchpadHeader = (_props: ILaunchpadHeader) => {
  const { convention, conventionRegistration, userCount } = useLaunchpadContext()

  const { classes } = useStyles()

  const { classes: headingClasses } = useHeadingStyles()

  const conventionRelativeTime = useRelativeTime({
    startAt: convention?.start_at,
    endAt: convention?.end_at,
  })

  const conventionAddress = getConventionAddress(convention)
  const shouldShowActiveUserCounter =
    convention.location_style === ConventionLocationStyle.VIRTUAL && conventionRelativeTime.during

  const activeTicketType = useActiveTicketType(convention, conventionRegistration)
  const registrationPaid = isRegistrationPaid(conventionRegistration)

  return (
    <div className={classes.root}>
      <div className={classes.heroContainer}>
        <img
          src={convention.hero_image_url}
          className={classes.heroImage}
          alt=""
          aria-hidden="true"
        />
      </div>
      <div className={classes.info}>
        <Typography variant="h1" marginBottom="0" className={headingClasses.h1alt}>
          {convention.name}
        </Typography>
        <Typography
          aria-hidden={!convention.location_name}
          variant="header5"
          component="p"
          color="gray.lightest"
          marginTop="30px">
          {convention.location_name}
        </Typography>
        <Typography
          aria-hidden={!conventionAddress}
          variant="paragraphRegular"
          component="p"
          color="gray.lightest"
          marginTop="5px">
          {conventionAddress}
        </Typography>
        <ConventionTime convention={convention} className={classes.time} />
        {!registrationPaid && activeTicketType && (
          <Typography variant="paragraphItalic" marginTop={pxToRem(20)} color="gray.lightest">
            {getFormattedPrice(activeTicketType.cost_usd_cents)}
          </Typography>
        )}
        <div className={classes.userCountBox}>
          {shouldShowActiveUserCounter && (
            <>
              <span className={classes.userCount}>{userCount}</span>
              {pluralize("person", userCount)} {pluralize("is", userCount)} here
            </>
          )}
        </div>
        <LaunchpadRegisterButton />
      </div>
    </div>
  )
}

export default LaunchpadHeader
